import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDocumentOverlay } from '@/components/document-overlay/DocumentDetailOverlayContext'

interface UseCaseDocumentsPageArgs {
  /** Refetches the parent case so the document count / metadata stays fresh. */
  refetchCase: () => void
}

interface UseCaseDocumentsPageResult {
  /** Currently selected document id (driven by the overlay state). */
  selectedDocId: string | null
  /** Whether the upload dialog is open. */
  uploadDialogOpen: boolean
  /** Sets the upload dialog open state. */
  setUploadDialogOpen: (open: boolean) => void
  /** Folder path the document list is currently browsing, if any. */
  currentFolderPath: string | undefined
  /** Sets the current folder path (fed by the document list). */
  setCurrentFolderPath: (path: string | undefined) => void
  /** Increments on every upload completion to force the list to refetch. */
  docListRefreshKey: number
  /** Opens a document in the overlay. */
  handleRowSelect: (docId: string | null) => void
  /** Bumps the list refresh key and refetches the case after an upload. */
  bumpDocListRefresh: () => void
}

/**
 * Container state for the case documents page: the upload deep link, the
 * document-list refresh key, folder path tracking and the row-select handler.
 * Keeps the page component focused on layout.
 *
 * The document overlay ↔ `?doc=<id>` URL sync (deep-link open, shareable link)
 * is owned centrally by `DocumentDetailOverlayProvider`, so this hook just opens
 * documents through the overlay and reads its state back.
 */
export function useCaseDocumentsPage({
  refetchCase,
}: UseCaseDocumentsPageArgs): UseCaseDocumentsPageResult {
  const [searchParams, setSearchParams] = useSearchParams()
  const overlay = useDocumentOverlay()

  const [uploadDialogOpen, setUploadDialogOpen] = useState(() => searchParams.get('upload') === '1')

  // URL-driven open: ?upload=1 auto-opens the uploader and is consumed so
  // refresh / back doesn't re-trigger.
  useEffect(() => {
    if (searchParams.get('upload') === '1') {
      setUploadDialogOpen(true)
      const next = new URLSearchParams(searchParams)
      next.delete('upload')
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, setSearchParams])

  const [currentFolderPath, setCurrentFolderPath] = useState<string | undefined>(undefined)
  const [docListRefreshKey, setDocListRefreshKey] = useState(0)

  const bumpDocListRefresh = useCallback(() => {
    setDocListRefreshKey(k => k + 1)
    void refetchCase()
  }, [refetchCase])

  const handleRowSelect = useCallback(
    (docId: string | null) => {
      if (docId) overlay.open(docId)
    },
    [overlay]
  )

  return {
    selectedDocId: overlay.documentId,
    uploadDialogOpen,
    setUploadDialogOpen,
    currentFolderPath,
    setCurrentFolderPath,
    docListRefreshKey,
    handleRowSelect,
    bumpDocListRefresh,
  }
}
